import { useState } from "react";
import { Info, ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const AIRBNB_CALENDAR_URL = "https://www.airbnb.com/calendar";

const STEPS = [
  "Go to your Airbnb account (Hosting, not Traveling).",
  "Click **Calendar**.",
  "On the left side, select your listing.",
  "On the right side, click **Availability settings**.",
  "Scroll down to **Connect calendars**.",
  "Click **Connect to another website**.",
  "Under Step 1, click **Copy** next to the URL, then paste it into Stravo.",
] as const;

interface IcalLearnHowProps {
  className?: string;
}

export function IcalLearnHow({ className }: IcalLearnHowProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Learn how to get your Airbnb iCal link"
        className={cn(
          "inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground hover:underline cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded",
          className
        )}
      >
        <Info className="h-4 w-4 shrink-0" aria-hidden />
        <span>Learn how</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>How to find your Airbnb iCal link</DialogTitle>
            <DialogDescription className="sr-only">
              Step-by-step instructions to copy your Airbnb calendar iCal URL into Stravo.
            </DialogDescription>
          </DialogHeader>
          <ol className="list-decimal list-inside space-y-2 text-left text-sm text-muted-foreground">
            {STEPS.map((step, i) => (
              <li key={i}>
                {step.split("**").map((part, j) =>
                  j % 2 === 1 ? (
                    <strong key={j} className="font-semibold text-foreground">
                      {part}
                    </strong>
                  ) : (
                    part
                  )
                )}
              </li>
            ))}
          </ol>
          <p className="text-xs text-muted-foreground">
            Tip: The iCal link is different for each listing—make sure you copy the one for the
            correct property.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <a
              href={AIRBNB_CALENDAR_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline inline-flex items-center gap-1"
            >
              Open Airbnb Calendar
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
          <DialogFooter className="pt-4 gap-2 sm:gap-0">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Close
            </Button>
            <Button onClick={() => setOpen(false)}>Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
